import { CartLinesUpdateEvent } from '@shopify/events';

(() => {
  const STORAGE_KEY = 'leamikette_wishlist_v1';
  const dialog = document.getElementById('la-wishlist-dialog');
  const itemsContainer = dialog?.querySelector('[data-la-wishlist-items]');
  const countLabel = dialog?.querySelector('[data-la-wishlist-label]');
  const addAllButton = dialog?.querySelector('[data-la-wishlist-add-all]');
  const feedback = dialog?.querySelector('[data-la-wishlist-feedback]');
  const status = document.querySelector('[data-la-wishlist-status]');

  let items = [];
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    if (Array.isArray(stored)) items = stored.filter((item) => item && item.id).slice(0, 100);
  } catch (_) {
    items = [];
  }

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);

  const safeUrl = (value = '') => {
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin || url.protocol === 'https:' ? url.href : '#';
    } catch (_) {
      return '#';
    }
  };

  const persist = () => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (_) {}
  };

  const announce = (message) => {
    if (!status) return;
    status.textContent = '';
    window.setTimeout(() => { status.textContent = message; }, 20);
  };

  const updateCount = () => {
    document.querySelectorAll('[data-la-wishlist-count]').forEach((badge) => {
      badge.textContent = String(items.length);
      badge.hidden = items.length === 0;
    });
    document.querySelectorAll('[data-la-wishlist-open]').forEach((button) => {
      const hasItems = items.length > 0;
      button.dataset.hasItems = String(hasItems);
      button.setAttribute('aria-label', hasItems
        ? `Apri la lista dei desideri: ${items.length} ${items.length === 1 ? 'prodotto salvato' : 'prodotti salvati'}`
        : 'Apri la lista dei desideri');
    });
    if (addAllButton) addAllButton.hidden = items.length === 0;
    if (countLabel) countLabel.textContent = `${items.length} ${items.length === 1 ? 'prodotto salvato' : 'prodotti salvati'}`;
  };

  const syncButtons = (root = document) => {
    root.querySelectorAll?.('[data-la-wishlist-toggle]').forEach((button) => {
      const active = items.some((item) => item.id === button.dataset.productId);
      button.setAttribute('aria-pressed', String(active));
      const title = button.dataset.productTitle || 'questo prodotto';
      button.setAttribute('aria-label', `${active ? 'Rimuovi' : 'Aggiungi'} ${title} ${active ? 'dalla' : 'alla'} lista dei desideri`);
    });
  };

  const emptyMarkup = () => `
    <div class="la-wishlist-empty">
      <span aria-hidden="true">♡</span>
      <h3>La tua lista è ancora vuota</h3>
      <p>Tocca il cuore sui prodotti che vuoi ritrovare qui.</p>
      <a href="/collections/i-nuovi-arrivi">Scopri i nuovi arrivi</a>
    </div>`;

  const render = () => {
    if (!itemsContainer) return;
    if (!items.length) {
      itemsContainer.innerHTML = emptyMarkup();
      updateCount();
      return;
    }

    itemsContainer.innerHTML = items.map((item) => {
      const url = safeUrl(item.url);
      const image = item.image ? `<img src="${escapeHtml(safeUrl(item.image))}" alt="${escapeHtml(item.title)}" width="184" height="232" loading="lazy">` : '';
      return `
        <article class="la-wishlist-item">
          <a class="la-wishlist-item__image" href="${escapeHtml(url)}">${image}</a>
          <div class="la-wishlist-item__content">
            <h3><a href="${escapeHtml(url)}">${escapeHtml(item.title)}</a></h3>
            <span class="la-wishlist-item__price">${escapeHtml(item.price)}</span>
            <div class="la-wishlist-item__actions">
              ${item.variantId ? `<button class="la-wishlist-item__add" type="button" data-la-wishlist-add="${escapeHtml(item.id)}">Aggiungi al carrello</button>` : ''}
              <a class="la-wishlist-item__link" href="${escapeHtml(url)}">Vedi prodotto <span aria-hidden="true">→</span></a>
            </div>
          </div>
          <button class="la-wishlist-item__remove" type="button" data-la-wishlist-remove="${escapeHtml(item.id)}" aria-label="Rimuovi ${escapeHtml(item.title)} dalla lista">×</button>
        </article>`;
    }).join('');
    updateCount();
  };

  const setFeedback = (message = '', type = '') => {
    if (!feedback) return;
    feedback.textContent = message;
    feedback.dataset.type = type;
    feedback.hidden = !message;
  };

  const setButtonLoading = (button, loading, label) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (!button.dataset.defaultLabel) button.dataset.defaultLabel = button.querySelector('span')?.textContent || button.textContent;
    button.disabled = loading;
    button.classList.toggle('is-loading', loading);
    const labelNode = button.querySelector('span') || button;
    labelNode.textContent = loading ? 'Aggiungo…' : (label || button.dataset.defaultLabel);
  };

  const addItemsToCart = async (wishlistItems, trigger) => {
    const cartItems = wishlistItems
      .filter((item) => item.variantId)
      .map((item) => ({ id: Number(item.variantId), quantity: 1 }))
      .filter((item) => Number.isFinite(item.id));

    if (!cartItems.length) {
      setFeedback('Apri il prodotto e scegli una variante disponibile.', 'error');
      return;
    }

    setFeedback('');
    setButtonLoading(trigger, true);

    const deferredEvent = CartLinesUpdateEvent.createPromise();
    const eventTarget = dialog || document;
    eventTarget.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'wishlist',
      lines: cartItems.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferredEvent.promise,
    }));

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: cartItems }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Prodotto non disponibile');

      const cartResponse = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Impossibile aggiornare il carrello');
      const cart = await cartResponse.json();

      dialog?.close();
      deferredEvent.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          itemCount: cartItems.length,
          source: 'leamikette-wishlist',
        },
      });
      announce(`${cartItems.length === 1 ? 'Prodotto aggiunto' : `${cartItems.length} prodotti aggiunti`} al carrello`);
    } catch (error) {
      deferredEvent.reject(error);
      setFeedback(error?.message || 'Non è stato possibile aggiungere al carrello. Riprova.', 'error');
      setButtonLoading(trigger, false);
    }
  };

  const toggle = (button) => {
    const id = button.dataset.productId;
    if (!id) return;
    const existingIndex = items.findIndex((item) => item.id === id);
    const title = button.dataset.productTitle || 'Prodotto';

    if (existingIndex >= 0) {
      items.splice(existingIndex, 1);
      announce(`${title} rimosso dalla lista dei desideri`);
    } else {
      items.unshift({
        id,
        variantId: button.dataset.variantId || '',
        title,
        url: button.dataset.productUrl || '',
        image: button.dataset.productImage || '',
        price: button.dataset.productPrice || ''
      });
      announce(`${title} aggiunto alla lista dei desideri`);
    }

    persist();
    syncButtons();
    render();
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const toggleButton = target.closest('[data-la-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      event.stopPropagation();
      toggle(toggleButton);
      return;
    }

    if (target.closest('[data-la-wishlist-open]')) {
      event.preventDefault();
      render();
      dialog?.showModal();
      return;
    }

    if (target.closest('[data-la-wishlist-close]')) {
      dialog?.close();
      return;
    }

    const removeButton = target.closest('[data-la-wishlist-remove]');
    if (removeButton) {
      const id = removeButton.getAttribute('data-la-wishlist-remove');
      const removed = items.find((item) => item.id === id);
      items = items.filter((item) => item.id !== id);
      persist();
      syncButtons();
      render();
      announce(`${removed?.title || 'Prodotto'} rimosso dalla lista dei desideri`);
      return;
    }

    const addButton = target.closest('[data-la-wishlist-add]');
    if (addButton) {
      const id = addButton.getAttribute('data-la-wishlist-add');
      const item = items.find((wishlistItem) => wishlistItem.id === id);
      if (item) addItemsToCart([item], addButton);
      return;
    }

    const addEverythingButton = target.closest('[data-la-wishlist-add-all]');
    if (addEverythingButton) {
      addItemsToCart(items, addEverythingButton);
    }
  });

  dialog?.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  let syncFrame;
  new MutationObserver(() => {
    cancelAnimationFrame(syncFrame);
    syncFrame = requestAnimationFrame(() => syncButtons());
  }).observe(document.body, { childList: true, subtree: true });

  syncButtons();
  render();
})();
